import { useAuth } from "@dl/web-core-js/auth";
import { Button, Input } from "@dl/web-core-js/ui";
import { useMutation } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { refusalMessage } from "../../api/manufacturing";
import type { OrderDTO } from "../../api/order";
import { NexeraOrderSearch } from "../../api/servicedesk";

/**
 * Finds the Nexera sale a service request answers: by the retailer's order
 * number (a Wayfair replacement suffix is dropped), by Nexera's own number, or
 * failing both by the customer's name. Each search scans SIGM's copy, so it
 * runs on demand, not as you type.
 */
export function NexeraOrderLookup({
  initial,
  onPick,
}: {
  initial?: string;
  onPick: (order: OrderDTO) => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const search = useMemo(() => new NexeraOrderSearch(http), [http]);
  const [term, setTerm] = useState(initial ?? "");
  const run = useMutation({ mutationFn: (value: string) => search.search(value) });

  return (
    <div className="flex flex-col gap-2">
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          run.mutate(term);
        }}
      >
        <Input
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder={t("sd.lookup.placeholder", "Retailer order number, Nexera order or customer name")}
        />
        <Button type="submit" variant="secondary" loading={run.isPending} disabled={term.trim() === ""}>
          <Search className="size-4" />
          {t("sd.lookup.search", "Find")}
        </Button>
      </form>
      {run.isError && <p className="text-[12px] text-risk">{refusalMessage(run.error, t("error.generic", "Something went wrong."))}</p>}
      {run.isSuccess && run.data.length === 0 && (
        <p className="text-[12px] text-ink-muted">{t("sd.lookup.none", "No Nexera order matches. The order can still be saved with the reference as typed.")}</p>
      )}
      {run.isSuccess && run.data.length > 0 && (
        <ul className="divide-y divide-line rounded-md border border-line">
          {run.data.map((order) => (
            <li key={order.id}>
              <button
                type="button"
                className="flex w-full flex-wrap items-baseline gap-x-3 px-3 py-2 text-left text-[13px] hover:bg-surface-sunk"
                onClick={() => {
                  onPick(order);
                  // Picked: the list has done its job.
                  run.reset();
                }}
              >
                <span className="font-medium tabular-nums">#{order.no}</span>
                <span>{order.client_order_no}</span>
                <span className="text-ink-muted">{order.ship_name}</span>
                <span className="text-ink-faint">{order.ship_city}</span>
                <span className="ml-auto text-[11px] text-ink-faint">
                  {[order.date, order.client?.cli_name1?.trim()].filter(Boolean).join(" · ")}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
